(function () {
  class IndarProductFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.options = Array.from(this.querySelectorAll('[data-filter-option]'));
      this.products = Array.from(this.querySelectorAll('[data-finder-product]'));
      this.filters = this.querySelector('.indar-finder__filters');
      this.groupOrder = ['animal', 'age', 'size', 'specific'];
      this.groups = new Map(this.groupOrder.map((group) => [
        group,
        this.querySelector(`[data-filter-group="${group}"]`),
      ]));
      this.clearButton = this.querySelector('[data-filter-clear]');
      this.moreButton = this.querySelector('[data-finder-more]');
      this.emptyState = this.querySelector('[data-finder-empty]');
      this.status = this.querySelector('[data-finder-status]');
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 8);
      this.visibleLimit = this.pageSize;
      this.active = new Map();

      this.productTags = new Map(this.products.map((product) => {
        let tags = [];
        try {
          tags = JSON.parse(product.dataset.productTags || '[]');
        } catch (error) {
          tags = [];
        }
        return [product, new Set(tags.map((tag) => this.normalize(tag)))];
      }));

      this.options.forEach((option) => {
        option.addEventListener('click', () => this.toggleOption(option));
      });
      this.clearButton?.addEventListener('click', () => this.clearFilters());
      this.moreButton?.addEventListener('click', () => {
        this.visibleLimit += this.pageSize;
        this.render();
      });

      this.restoreFromUrl();
      this.render();
    }

    normalize(value) {
      return String(value || '')
        .trim()
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    }

    valuesFor(option) {
      return String(option.dataset.filterValues || '')
        .split(',')
        .map((value) => this.normalize(value))
        .filter(Boolean);
    }

    optionMatches(product, option) {
      const tags = this.productTags.get(product) || new Set();
      return this.valuesFor(option).some((value) => tags.has(value));
    }

    productMatches(product, active = this.active) {
      return Array.from(active.values()).every((option) => this.optionMatches(product, option));
    }

    toggleOption(option) {
      const group = option.dataset.filterGroup;
      const isActive = option.getAttribute('aria-pressed') === 'true';
      const groupIndex = this.groupOrder.indexOf(group);

      this.groupOrder.slice(groupIndex + 1).forEach((followingGroup) => {
        this.active.delete(followingGroup);
        this.options
          .filter((candidate) => candidate.dataset.filterGroup === followingGroup)
          .forEach((candidate) => candidate.setAttribute('aria-pressed', 'false'));
      });

      this.options
        .filter((candidate) => candidate.dataset.filterGroup === group)
        .forEach((candidate) => candidate.setAttribute('aria-pressed', 'false'));

      if (isActive) {
        this.active.delete(group);
      } else {
        option.setAttribute('aria-pressed', 'true');
        this.active.set(group, option);
      }

      this.visibleLimit = this.pageSize;
      this.updateUrl();
      this.render();
    }

    clearFilters() {
      this.active.clear();
      this.options.forEach((option) => option.setAttribute('aria-pressed', 'false'));
      this.visibleLimit = this.pageSize;
      this.updateUrl();
      this.render();
    }

    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      this.groupOrder.forEach((group, index) => {
        if (index > 0 && !this.active.has(this.groupOrder[index - 1])) return;
        const key = params.get(`indar_${group}`);
        if (!key) return;
        const option = this.options.find((candidate) => (
          candidate.dataset.filterGroup === group && candidate.dataset.filterKey === key
        ));
        if (!option) return;
        option.setAttribute('aria-pressed', 'true');
        this.active.set(group, option);
      });
    }

    updateUrl() {
      const url = new URL(window.location.href);
      this.groupOrder.forEach((group) => url.searchParams.delete(`indar_${group}`));
      this.active.forEach((option, group) => {
        url.searchParams.set(`indar_${group}`, option.dataset.filterKey);
      });
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    updateOptionCounts() {
      this.options.forEach((option) => {
        const group = option.dataset.filterGroup;
        const groupIndex = this.groupOrder.indexOf(group);
        const testActive = new Map(Array.from(this.active).filter(([activeGroup]) => (
          this.groupOrder.indexOf(activeGroup) < groupIndex
        )));
        testActive.set(group, option);
        const count = this.products.filter((product) => this.productMatches(product, testActive)).length;
        const countElement = option.querySelector('[data-option-count]');
        if (countElement) countElement.textContent = String(count);
        const isActive = option.getAttribute('aria-pressed') === 'true';
        option.disabled = count === 0 && !isActive;
      });
    }

    updateStepVisibility() {
      let visibleGroups = 1;

      this.groupOrder.forEach((group, index) => {
        const groupElement = this.groups.get(group);
        if (!groupElement) return;
        const isVisible = index === 0 || this.active.has(this.groupOrder[index - 1]);
        groupElement.hidden = !isVisible;
        if (isVisible && index > 0) visibleGroups += 1;
      });

      if (this.filters) this.filters.dataset.visibleGroups = String(visibleGroups);
    }

    render() {
      const matching = this.products.filter((product) => this.productMatches(product));
      const matchingSet = new Set(matching);

      this.products.forEach((product) => {
        const matchingIndex = matching.indexOf(product);
        product.hidden = !matchingSet.has(product) || matchingIndex >= this.visibleLimit;
      });

      if (this.status) {
        this.status.textContent = matching.length === 1
          ? '1 producto encontrado'
          : `${matching.length} productos encontrados`;
      }
      if (this.emptyState) this.emptyState.hidden = matching.length !== 0;
      if (this.clearButton) this.clearButton.hidden = this.active.size === 0;
      if (this.moreButton) this.moreButton.hidden = matching.length <= this.visibleLimit;

      this.updateStepVisibility();
      this.updateOptionCounts();
    }
  }

  if (!customElements.get('indar-product-finder')) {
    customElements.define('indar-product-finder', IndarProductFinder);
  }
})();
